import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Heart, Bed, Bath, Home } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useLocalizedNavigation } from '../hooks/useLocalizedNavigation';
import { Button } from './ui/button';
import { Property } from '../types';

interface PropertyCardProps {
  property: Property;
}

const PropertyCard: React.FC<PropertyCardProps> = ({ property }) => {
  const { t } = useTranslation();
  const { user, toggleSaveProperty, isPropertySaved } = useAuth();
  const { getTextInCurrentLanguage } = useLanguage();
  const { getLocalizedPath } = useLocalizedNavigation();
  const isSaved = isPropertySaved(property.id);

  const getTranslatedListingType = (listingType: string): string => {
    const key = listingType.toLowerCase().replace(' ', '');
    if (key === 'forsale') return t('listingTypes.forSale');
    if (key === 'forrent') return t('listingTypes.forRent');
    return listingType;
  };

  const getTranslatedPropertyType = (propertyType: string): string => {
    const key = propertyType.toLowerCase();
    const typeMap: { [key: string]: string } = {
      'house': t('propertyTypes.house'),
      'apartment': t('propertyTypes.apartment'),
      'condo': t('propertyTypes.condo'),
      'townhouse': t('propertyTypes.townhouse'),
      'land': t('propertyTypes.land')
    };
    return typeMap[key] || propertyType;
  };

  const formatPrice = (price: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(price);
  };

  const handleSaveClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    if (user) {
      toggleSaveProperty(property.id);
    }
  };

  return (
    <Link to={getLocalizedPath(`/property/${property.id}`)} className="group block">
      <div className="bg-white rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
        {/* Image */}
        <div className="relative h-56 overflow-hidden">
          <img
            src={property.mainImage}
            alt={getTextInCurrentLanguage(property.title)}
            className={`w-full h-full object-cover transition-transform duration-300 group-hover:scale-110 ${property.sold ? 'opacity-75' : ''}`}
          />
          {property.sold && (
            <div className="absolute top-0 left-0 right-0 bg-red-600 text-white text-center py-2 font-bold text-sm uppercase tracking-wide z-10">
              {t('common.sold')}
            </div>
          )}
          {user && (
            <Button
              onClick={handleSaveClick}
              variant="ghost"
              size="icon"
              className={`absolute top-3 right-3 rounded-full bg-white/90 hover:bg-white transition-all ${
                isSaved ? 'text-red-500' : 'text-gray-600'
              }`}
            >
              <Heart
                className={`h-5 w-5 ${
                  isSaved ? 'fill-current' : ''
                }`}
              />
            </Button>
          )}
          <div className="absolute bottom-3 left-3 bg-blue-600 text-white px-3 py-1 rounded-full text-sm font-semibold">
            {getTranslatedListingType(property.listingType)}
          </div>
        </div>

        {/* Content */}
        <div className="p-4 sm:p-5">
          <div className="flex items-start justify-between mb-2">
            <h3 className="text-xl sm:text-2xl font-bold text-gray-900">
              {formatPrice(property.price)}
            </h3>
          </div>

          <h4 className="text-base sm:text-lg font-semibold text-gray-800 mb-2 line-clamp-1">
            {getTextInCurrentLanguage(property.title)}
          </h4>

          <p className="text-gray-600 text-xs sm:text-sm mb-4 line-clamp-2">
            {property.address}, {property.city}, {property.state} {property.zipCode}
          </p>

          {/* Property Stats */}
          <div className="flex items-center gap-3 sm:gap-4 text-gray-600 border-t border-gray-100 pt-4">
            <div className="flex items-center gap-1">
              <Bed className="h-4 w-4 flex-shrink-0" />
              <span className="text-xs sm:text-sm font-medium">{property.beds} {t('common.beds').toLowerCase().slice(0, 3)}</span>
            </div>
            <div className="flex items-center gap-1">
              <Bath className="h-4 w-4 flex-shrink-0" />
              <span className="text-xs sm:text-sm font-medium">{property.baths} {t('common.baths').toLowerCase().slice(0, 3)}</span>
            </div>
            <div className="flex items-center gap-1">
              <Home className="h-4 w-4 flex-shrink-0" />
              <span className="text-xs sm:text-sm font-medium">{property.sqft.toLocaleString()} {t('common.sqft')}</span>
            </div>
          </div>

          <div className="mt-3 sm:mt-4 inline-block px-2 sm:px-3 py-1 bg-gray-100 rounded-full">
            <span className="text-xs font-medium text-gray-700">{getTranslatedPropertyType(property.propertyType)}</span>
          </div>
        </div>
      </div>
    </Link>
  );
};

export default PropertyCard;
